import React, { useState, useContext, useEffect, createRef } from "react";
import { ResultsContainer } from "../ResultsContainer/ResultsContainer";
import { StrategyTabIndices } from "../../shared/constants";
import { InvestmentDTO } from "../../model/investmentDTO";
import { DatePresetsContext } from "../Store/DatePresetsContext";
import { CalculatorResultsContext } from "../Store/CalculatorResultsContext";
import { RestartOrEditSettings } from "../CalculatorResults/RestartOrEditSettings";
import { pipe } from "fp-ts/lib/pipeable";
import { fold } from "fp-ts/lib/Option";
import { CalculatorStepsContext } from "../Store/CalculatorStepsContext";
import {
  analyticsStrategyViewEvent,
  analyticsViewChangeEvent,
} from "../../utils/Analytics";

interface ResultsPageProps {
  resultsView: string;
}

export const CalculatorResultsPage = (props: ResultsPageProps) => {
  let arrLength = 0;
  const datePresetStore = useContext(DatePresetsContext);
  const resultsStore = useContext(CalculatorResultsContext);
  const [currentTab, setCurrentTab] = useState(StrategyTabIndices.Strategy1);

  // eslint-disable-next-line
  const [elRefs, setElRefs] = useState<any[]>([]);
  const { steps } = useContext(CalculatorStepsContext);

  const handleNavTabClick = (
    newTabIndex: React.SetStateAction<StrategyTabIndices>
  ) => {
    setCurrentTab(currentTab !== newTabIndex ? newTabIndex : currentTab);
    datePresetStore.setDatePresets(
      datePresetStore.datePresets.withFetchError(false)
    );

    const index = newTabIndex.valueOf();
    if (typeof index === "number") {
      analyticsStrategyViewEvent(index as number, steps.inputs, steps.config);
    }
  };

  const handleViewChangedAnalyics = (viewType: string) => {
    const index = currentTab as number;
    analyticsViewChangeEvent(index, viewType, steps.inputs, steps.config);
  };

  /* trigger analytics whenever resultsView toggle changes, don't trigger on first load */
  const [firstLoad, setFirstLoad] = useState(true);
  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    } else {
      handleViewChangedAnalyics(props.resultsView);
    }
  }, [props.resultsView]);

  const noData: Array<InvestmentDTO> = [];
  const chartData: Array<InvestmentDTO> = pipe(
    resultsStore.results,
    fold(
      () => noData,
      (results) => results.investments
    )
  );

  useEffect(() => {
    arrLength = chartData.length;
    setElRefs((elRefs) =>
      Array(arrLength)
        .fill(arrLength)
        .map((_, i) => elRefs[i] || createRef())
    );
  }, [arrLength]);

  const handleNextTab = (
    firstTabInRound: number,
    nextTab: number,
    lastTabInRound: number
  ) => {
    const tabToSelect =
      currentTab === lastTabInRound ? firstTabInRound : nextTab;
    handleNavTabClick(tabToSelect);
    elRefs[tabToSelect].current.focus();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    const tabCount = chartData.length - 1;

    if (event.key === "ArrowLeft") {
      const last = tabCount;
      const next = currentTab - 1;
      handleNextTab(last, next, 0);
    }
    if (event.key === "ArrowRight") {
      const first = 0;
      const next = currentTab + 1;
      handleNextTab(first, next, tabCount);
    }
  };

  return (
    <>
      {chartData.length > 0 ? (
        <>
          <ul id="resultsTab" className="nav nav-tabs" role="tablist">
            {chartData.map((dt, indx) => {
              return (
                <li
                  id={"strategy-" + (indx + 1) + "-parent"}
                  key={"Strategy-" + dt.investmentIdentifier}
                  className={currentTab === indx ? "active" : ""}
                >
                  <a
                    href="#"
                    onClick={() => handleNavTabClick(indx)}
                    onKeyDown={handleKeyPress}
                    role="tab"
                    data-toggle="tab"
                    ref={elRefs[indx]}
                    aria-selected={currentTab === indx}
                    tabIndex={currentTab === indx ? 0 : -1}
                    aria-controls={"strategy-" + (indx + 1) + "-panel"}
                  >
                    {"Strategy " + (indx + 1)}
                  </a>
                </li>
              );
            })}
          </ul>
          <div id="resultsTabContent">
            <div
              role="tabpanel"
              aria-labelledby={"strategy-" + (currentTab + 1) + "-parent"}
              id={"strategy-" + (currentTab + 1) + "-panel"}
            >
              <ResultsContainer
                currentStrategyData={chartData[currentTab]}
                view={props.resultsView}
              ></ResultsContainer>
            </div>
          </div>
        </>
      ) : (
        <span>
          Chart Not Available for the requested data.
          <RestartOrEditSettings />
        </span>
      )}
    </>
  );
};
